// pages/Oati/OattRatesPage.tsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Select, Table, InputNumber, Input, Spin, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation } from '@apollo/client';
import { GET_OATT_RATES, UPDATE_OATT_RATES } from '@graphql/oattRates.gql';
import PageLayout from '@components/layout/PageLayout';
import styles from './OattRates.module.css';
import { NSPButton } from '@components/ui/NSPButton';
import useSession from '@hooks/useSession';
import { toTestAttrValue, withTestColumnAttrs, type TestAttrs } from '@utils/testAttr';

const { Option } = Select;

// ─── Types ────────────────────────────────────────────────────────────────────

export type RatePageType = 'point-to-point' | 'network';

interface OattRate {
    oatt_rate_id: number;
    type: string;
    service_type: string;
    transmission_service_type: string | null;
    transmission_details: string | null;
    ancillary_service_type: string | null;
    ancillary_service_details: string | null;
    frequency: string | null;
    rate: number | null;
    comments: string | null;
    updated_at: string | null;
}

interface RateRow extends OattRate {
    key: number | string;
    isNA: boolean;
    pendingRate: number | null;
    isGroupHeader?: boolean;
    groupLabel?: string;
}

// ─── Frequency helpers ────────────────────────────────────────────────────────

/**
 * Maps raw DB frequency keys → human-readable period labels
 * matching the HTML reference: "Yearly", "Monthly", "On-Peak Daily", etc.
 */
const FREQUENCY_LABELS: Record<string, string> = {
    years: 'Yearly',
    monthly: 'Monthly',
    weekly: 'Weekly',
    daily_on_peak: 'On-Peak Daily',
    daily_off_peak: 'Off-Peak Daily',
    hourly_on_peak: 'On-Peak Hourly',
    hourly_off_peak: 'Off-Peak Hourly',
    daily: 'Daily',
    hourly: 'Hourly',
};

/**
 * Maps raw DB frequency keys → unit strings ($/MW/...)
 */
const FREQUENCY_UNITS: Record<string, string> = {
    years: '/MW/Year',
    monthly: '/MW/Month',
    weekly: '/MW/Week',
    daily_on_peak: '/MW/Day',
    daily_off_peak: '/MW/Day',
    daily: '/MW/Day',
    hourly_on_peak: '/MW/Hour',
    hourly_off_peak: '/MW/Hour',
    hourly: '/MW/Hour',
};

function getFrequencyLabel(frequency: string | null): string {
    if (!frequency) return '—';
    return FREQUENCY_LABELS[frequency] ?? frequency;
}

function getFrequencyUnit(frequency: string | null): string {
    if (!frequency) return '—';
    return FREQUENCY_UNITS[frequency] ?? '—';
}

// ─── Dropdown configs ─────────────────────────────────────────────────────────

/**
 * Each schedule entry maps:
 *   value        → the <Select> option value (used internally)
 *   label        → display text
 *   filterField  → which OattRate field to match against
 *   filterValue  → the exact value from the BE to match
 */
interface ScheduleConfig {
    value: string;
    label: string;
    filterField: 'transmission_service_type' | 'ancillary_service_type';
    filterValue: string;
    scheduleDetail?: string;
}

const PTP_TRANS_SCHEDULES: ScheduleConfig[] = [
    {
        value: 'sched7',
        label: 'Schedule 7 – Firm',
        filterField: 'transmission_service_type',
        filterValue: 'Firm',
    },
    {
        value: 'sched8',
        label: 'Schedule 8 – Non-Firm',
        filterField: 'transmission_service_type',
        filterValue: 'Non-Firm',
    },
];

const NETWORK_TRANS_SCHEDULES: ScheduleConfig[] = [
    {
        value: 'sched10',
        label: 'Network Integration Firm – Schedule 10',
        filterField: 'transmission_service_type',
        filterValue: 'Network Integration Firm',
    },
    {
        value: 'sched11',
        label: 'Network Integration Firm – Schedule 11',
        filterField: 'transmission_service_type',
        filterValue: 'Network Integration Non-Firm',
    },
];

const ANCILLARY_SCHEDULES: ScheduleConfig[] = [
    {
        value: 'sched1',
        label: 'Scheduling, System Control, and Dispatch Services – Schedule 1',
        filterField: 'ancillary_service_type',
        filterValue: 'Scheduling, System Control, and Dispatch Services',
        scheduleDetail: 'Schedule 1',
    },
    {
        value: 'sched2',
        label: 'Reactive Supply and Voltage Control From Generation Sources Service – Schedule 2',
        filterField: 'ancillary_service_type',
        filterValue: 'Reactive Supply and Voltage Control From Generation Sources Service',
        scheduleDetail: 'Schedule 2',
    },
    {
        value: 'sched3',
        label: 'Regulation and Frequency Response Service – Schedule 3',
        filterField: 'ancillary_service_type',
        filterValue: 'Regulation and Frequency Response Service',
        scheduleDetail: 'Schedule 3',
    },
    {
        value: 'sched5',
        label: 'Operating Reserve – Spinning Reserve Service – Schedule 5',
        filterField: 'ancillary_service_type',
        filterValue: 'Operating Reserve - Spinning Reserve Service',
        scheduleDetail: 'Schedule 5',
    },
    {
        value: 'sched6',
        label: 'Operating Reserve – Supplemental Reserve Service – Schedule 6',
        filterField: 'ancillary_service_type',
        filterValue: 'Operating Reserve - Supplemental',
        scheduleDetail: 'Schedule 6',
    },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getAllSchedules(pageType: RatePageType): ScheduleConfig[] {
    return [
        ...(pageType === 'point-to-point' ? PTP_TRANS_SCHEDULES : NETWORK_TRANS_SCHEDULES),
        ...ANCILLARY_SCHEDULES,
    ];
}

function getScheduleConfig(pageType: RatePageType, schedKey: string): ScheduleConfig | undefined {
    return getAllSchedules(pageType).find((s) => s.value === schedKey);
}

// ─── Component ────────────────────────────────────────────────────────────────

interface OattRatesPageProps {
    pageType: RatePageType;
}

const OattRatesPage = ({ pageType }: OattRatesPageProps) => {
    const navigate = useNavigate();
    const { isOattRateOwner } = useSession();

    const [selectedService, setSelectedService] = useState<string>('');
    const [selectedSchedule, setSelectedSchedule] = useState<string>('');
    const [pendingEdits, setPendingEdits] = useState<Record<number, number | null>>({});
    const [pendingComments, setPendingComments] = useState<Record<number, string>>({});
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

    const title = pageType === 'point-to-point' ? 'OATT Point-to-Point Rates' : 'OATT Network Rates';

    const { data, loading, error, refetch } = useQuery<{ oattRates: OattRate[] }>(GET_OATT_RATES, {
        variables: { type: pageType },
        skip: !selectedService || !selectedSchedule,
        fetchPolicy: 'cache-and-network',
    });

    const [updateRates, { loading: updating }] = useMutation(UPDATE_OATT_RATES);

    useEffect(() => {
        setSelectedSchedule('');
        setPendingEdits({});
    }, [selectedService]);

    useEffect(() => {
        setPendingEdits({});
        setPendingComments({});
        setFeedback(null);
    }, [selectedSchedule]);

    // ── Derive visible rows ────────────────────────────────────────────────────
    const visibleRows: RateRow[] = (() => {
        if (!data?.oattRates || !selectedService || !selectedSchedule) return [];

        const schedConfig = getScheduleConfig(pageType, selectedSchedule);
        if (!schedConfig) return [];

        const filtered: RateRow[] = data.oattRates
            .filter((r) => {
                if (r.service_type.toLowerCase() !== selectedService) return false;
                if (!(r[schedConfig.filterField] ?? '').startsWith(schedConfig.filterValue)) return false;
                // Also filter by schedule detail (e.g. 'Schedule 3') to prevent cross-schedule leakage
                if (schedConfig.scheduleDetail && r.ancillary_service_details !== schedConfig.scheduleDetail) return false;
                return true;
            })
            .map((r) => ({
                ...r,
                key: r.oatt_rate_id,
                isNA: r.rate === null || r.rate === 0,
                pendingRate: pendingEdits[r.oatt_rate_id] !== undefined
                    ? pendingEdits[r.oatt_rate_id]
                    : r.rate,
            }));

        // Group by ancillary_service_type (holds the sub-label e.g. '...Regulation' vs '...Load Following')
        const subTypeKey = (r: RateRow) => r.ancillary_service_type ?? r.transmission_details ?? '';

        const groups = filtered.reduce<Map<string, RateRow[]>>((acc, row) => {
            const k = subTypeKey(row);
            if (!acc.has(k)) acc.set(k, []);
            acc.get(k)!.push(row);
            return acc;
        }, new Map());

        if (groups.size <= 1) return filtered;

        const result: RateRow[] = [];
        for (const [label, rows] of groups) {
            if (label) {
                result.push({
                    ...rows[0],
                    key: `header-${label}`,   // unique string key — fixes duplicate key error
                    isGroupHeader: true,
                    groupLabel: label,
                });
            }
            result.push(...rows);
        }
        return result;
    })();

    // ── Handle rate input change ───────────────────────────────────────────────
    const handleRateChange = useCallback((id: number, value: number | null) => {
        setPendingEdits((prev) => ({ ...prev, [id]: value }));
    }, []);

    const handleCommentChange = useCallback((id: number, value: string) => {
        setPendingComments((prev) => ({ ...prev, [id]: value }));
    }, []);

    // ── Update rates ──────────────────────────────────────────────────────────
    const handleUpdateRates = async () => {
        const editedIds = Object.entries(pendingEdits)
            .filter(([, v]) => v !== null)
            .map(([id]) => Number(id));

        if (editedIds.length === 0) {
            setFeedback({ type: 'info', text: 'No changes to save.' });
            return;
        }

        const missingComment = editedIds.some((id) => !(pendingComments[id] ?? '').trim());
        if (missingComment) {
            setFeedback({ type: 'error', text: 'A comment is required for every rate you are updating.' });
            return;
        }

        const updates = editedIds.map((id) => ({
            oatt_rate_id: id,
            rate: pendingEdits[id] as number,
            comments: pendingComments[id].trim(),
        }));

        try {
            const { data: result } = await updateRates({ variables: { updates } });
            const res = result?.updateOattRates;
            if (res?.success) {
                setFeedback({ type: 'success', text: res.message ?? 'Rates updated successfully.' });
                setPendingEdits({});
                setPendingComments({});
                refetch();
            } else {
                setFeedback({ type: 'error', text: res?.message ?? 'Update failed.' });
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Unknown error';
            setFeedback({ type: 'error', text: `Update failed: ${msg}` });
        }
    };

    // ── Table columns ─────────────────────────────────────────────────────────
    const columns: ColumnsType<RateRow> = [
        {
            title: 'Period',
            dataIndex: 'frequency',
            key: 'frequency',
            width: 160,
            onCell: (record) => record.isGroupHeader ? { colSpan: 4, className: styles.groupHeaderCell } : {},
            render: (frequency: string | null, record) => {
                if (record.isGroupHeader) return <span className={styles.groupHeaderLabel}>{record.groupLabel}</span>;
                return getFrequencyLabel(frequency);
            },
        },
        {
            title: 'Rate',
            key: 'rate',
            width: 180,
            onCell: (record) => record.isGroupHeader ? { colSpan: 0 } : {},
            render: (_, record) => {
                if (record.isGroupHeader) return null;
                if (record.isNA) {
                    return <span className={styles.naLabel} data-testid="rate-na">N/A</span>;
                }
                if (!isOattRateOwner) {
                    return (
                        <span className={styles.rateReadOnly} data-testid="rate-read-only">
                            ${Number(record.rate ?? 0).toFixed(2)}
                        </span>
                    );
                }
                return (
                    <div className={styles.rateInputWrapper}>
                        <span className={styles.currencyPrefix}>$</span>
                        <InputNumber
                            className={styles.rateInput}
                            min={0}
                            step={0.01}
                            precision={2}
                            type="number"
                            value={record.pendingRate ?? record.rate ?? undefined}
                            onChange={(val) => handleRateChange(record.oatt_rate_id, val)}
                            controls={false}
                            data-testid="rate-input"
                        />
                    </div>
                );
            },
        },
        {
            title: 'Unit',
            key: 'unit',
            width: 140,
            onCell: (record) => record.isGroupHeader ? { colSpan: 0 } : {},
            render: (_, record) => {
                if (record.isGroupHeader) return null;
                return getFrequencyUnit(record.frequency);
            },
        },
        {
            title: 'Comments',
            key: 'comments',
            width: 260,
            onCell: (record) => record.isGroupHeader ? { colSpan: 0 } : {},
            render: (_, record) => {
                if (record.isGroupHeader) return null;
                if (record.isNA) return null;
                if (!isOattRateOwner) {
                    return <span className={styles.rateReadOnly} data-testid="rate-comment">{record.comments ?? '—'}</span>;
                }
                const hasPendingEdit = pendingEdits[record.oatt_rate_id] !== undefined;
                const commentValue = pendingComments[record.oatt_rate_id] ?? record.comments ?? '';
                const isMissing = hasPendingEdit && !commentValue.trim();
                return (
                    <Input
                        className={`${styles.commentInput} ${isMissing ? styles.commentInputError : ''}`}
                        value={commentValue}
                        onChange={(e) => handleCommentChange(record.oatt_rate_id, e.target.value.replace(/[^\x20-\x7E -￿\uD800-\uDFFF]/g, ''))}
                        maxLength={200}
                        data-testid="rate-comment-input"
                        data-invalid={String(isMissing)}
                    />
                );
            },
        },
    ];

    const hasSubs = visibleRows.some((r) => r.ancillary_service_details);
    const tableColumns = hasSubs ? columns : columns.filter((c) => c.key !== 'sub');

    const scheduleLabel = selectedSchedule
        ? getScheduleConfig(pageType, selectedSchedule)?.label ?? ''
        : '';

    const transSchedules = pageType === 'point-to-point' ? PTP_TRANS_SCHEDULES : NETWORK_TRANS_SCHEDULES;

    return (
        <PageLayout title={title}>
            <div className={styles.page} data-testid="oatt-rates" data-page-type={pageType}>
                {/* ── Selectors ── */}
                <div className={styles.selectorPanel}>
                    <div className={styles.selectorRow}>
                        <label htmlFor="select-service" className={styles.selectorLabel}>Select Service</label>
                        <Select
                            id="select-service"
                            data-testid="service-select"
                            className={styles.selectorInput}
                            placeholder="-- Select --"
                            value={selectedService || undefined}
                            onChange={(val) => setSelectedService(val)}
                            allowClear
                        >
                            <Option value="transmission" data-testid="service-option" data-value="transmission">Transmission Services</Option>
                            <Option value="ancillary" data-testid="service-option" data-value="ancillary">Ancillary Services</Option>
                        </Select>
                    </div>

                    {selectedService === 'transmission' && (
                        <div className={styles.selectorRow}>
                            <label htmlFor="select-trans-schedule" className={styles.selectorLabel}>Select Transmission Schedule</label>
                            <Select
                                id="select-trans-schedule"
                                data-testid="transmission-schedule-select"
                                className={styles.selectorInput}
                                placeholder="-- Select --"
                                value={selectedSchedule || undefined}
                                onChange={(val) => setSelectedSchedule(val)}
                                allowClear
                            >
                                {transSchedules.map((s) => (
                                    <Option key={s.value} value={s.value} data-testid="schedule-option" data-value={s.value}>
                                        {s.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}

                    {selectedService === 'ancillary' && (
                        <div className={styles.selectorRow}>
                            <label htmlFor="select-ancillary-schedule" className={styles.selectorLabel}>Select Ancillary Schedule</label>
                            <Select
                                id="select-ancillary-schedule"
                                data-testid="ancillary-schedule-select"
                                className={styles.selectorInput}
                                placeholder="-- Select an Option --"
                                value={selectedSchedule || undefined}
                                onChange={(val) => setSelectedSchedule(val)}
                                allowClear
                            >
                                {ANCILLARY_SCHEDULES.map((s) => (
                                    <Option key={s.value} value={s.value} data-testid="schedule-option" data-value={s.value}>
                                        {s.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}
                </div>

                {/* ── Feedback Alert ── */}
                {feedback && (
                    <div style={{ width: '100%', maxWidth: 860 }}>
                        <Alert
                            type={feedback.type}
                            message={feedback.text}
                            showIcon
                            closable
                            onClose={() => setFeedback(null)}
                            data-testid="rates-feedback-alert"
                            data-type={feedback.type}
                        />
                    </div>
                )}

                {/* ── Rates Table ── */}
                {selectedSchedule && (
                    <div className={styles.tableSection}>
                        <div className={styles.tableHeader}>
                            <h3 className={styles.tableTitle} data-testid="rates-table-title" data-schedule={selectedSchedule}>{scheduleLabel}</h3>
                        </div>

                        {error ? (
                            <Empty description={`Error loading rates: ${error.message}`} data-testid="rates-error" />
                        ) : loading ? (
                            <div className={styles.spinnerWrapper} data-testid="rates-loading">
                                <Spin size="large" />
                            </div>
                        ) : visibleRows.length === 0 ? (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                data-testid="rates-empty"
                                description="No rate records found for this schedule. Please seed the oatt_rates table."
                            />
                        ) : (
                            <Table<RateRow>
                                dataSource={visibleRows}
                                columns={withTestColumnAttrs(tableColumns, 'rate-value')}
                                rowKey={(record) => String(record.key)}
                                pagination={false}
                                bordered
                                size="middle"
                                className={styles.ratesTable}
                                data-testid="rates-table"
                                onRow={(record) =>
                                    (record.isGroupHeader
                                        ? { 'data-testid': 'rate-group-row', 'data-group': toTestAttrValue(record.groupLabel) }
                                        : {
                                            'data-testid': 'rate-row',
                                            'data-rate-id': String(record.oatt_rate_id),
                                            'data-frequency': record.frequency ?? '',
                                            'data-na': String(record.isNA),
                                        }) as TestAttrs
                                }
                            />
                        )}
                    </div>
                )}

                {/* ── Action Buttons ── */}
                <div className={styles.actionButtons}>
                    <NSPButton onClick={() => navigate('/oati/core-info/rates')} data-testid="back-to-core-info-button">← Back to Core Info</NSPButton>
                    {isOattRateOwner && (
                        <NSPButton
                            loading={updating}
                            disabled={!selectedSchedule || Object.keys(pendingEdits).length === 0}
                            onClick={handleUpdateRates}
                            data-testid="update-rates-button"
                        >
                            Update Rates
                        </NSPButton>
                    )}
                </div>
            </div>
        </PageLayout>
    );
};

export default OattRatesPage;